import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './GiftCardAmount.module.css';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from '../../../context/GlobalContext';

// Import the FieldError component
const FieldError = ({ error, id }) => {
  if (!error) return null;
  return (
    <div className="field-error" id={`error-${id}`} role="alert">
        <span className="error-icon" aria-hidden="true">⚠️</span>
        <span className="error-message">{error}</span>
      </div>
  );
};

const GiftCardAmount = ({ 
  offers = [], 
  selectedStore, 
  onSelectAmount, 
  selectedAmount, 
  lang = 'fr', 
  store 
}) => {
  const { t } = useTranslation();
  const [customActive, setCustomActive] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [hasBlurred, setHasBlurred] = useState(false);
  const [isCustomValid, setIsCustomValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState(''); // Add error message state
  const { currentLanguage, formatAmounts } = useGlobalContext();

  const isAmexSelected = store.useAmex || false;
  const amountOptions = [];
  const customInputRef = useRef(null);
  const isAvailableAmount = (amount) => {
    let available = false;
    offers.forEach((offer) => {
      if (offer) {
        if (
          offer.type === 'single' &&
          offer.value === amount &&
          offer.availability &&
          offer.availability[selectedStore] > 0
        ) {
          available = true;
        } else if (offer.type === 'slider' && offer.values) {
          offer.values.forEach((val) => {
            if (
              val.value === amount &&
              val.availability &&
              val.availability[selectedStore] > 0
            ) {
              available = true;
            }
          });
        } else if (
          offer.type === 'manual' &&
          amount !== null &&
          amount !== '' &&
          Number(amount) >= offer.min &&
          (!offer.max || Number(amount) <= offer.max)
        ) {
          available = true;
        }
      }
    });
    return available;
  };


  offers.forEach((offer, index) => {
    if (index === 3 && !isAmexSelected) {
      return;
    }

    if (offer && offer.type === 'single' && offer.value != null && isAvailableAmount(offer.value)) {
      amountOptions.push({
        value: offer.value,
        label: formatAmounts(offer.value),
        isAmex: index === 3,
        
      });
    } else if (offer && offer.type === 'slider' && offer.values && offer.values.length > 0) {
      offer.values.forEach((val) => {
        if (isAvailableAmount(val.value)) {
          amountOptions.push({
            value: val.value,
            label:formatAmounts(val.value),
            isAmex: index === 3
          });
        }
      });
    }
  });

  const manualOffer = offers.find((offer) => offer && offer.type === 'manual');

  // Si aucun montant n'est sélectionné et qu'il existe des options, on sélectionne la première par défaut.
  useEffect(() => {
    if ((selectedAmount === null || selectedAmount === undefined || selectedAmount === '') && amountOptions.length > 0) {
      onSelectAmount(amountOptions[0].value);
    }
  }, [selectedAmount, amountOptions, onSelectAmount]);

  const handleCustomButtonClick = () => {
    setCustomActive(true);
    onSelectAmount(null);
    setErrorMessage(''); // Clear any error messages
  };

  const handleCustomValueChange = (e) => {
    const value = e.target.value;
    setCustomValue(value);
    setIsCustomValid(null); // Réinitialiser la validation pendant la saisie
    setErrorMessage(''); // Clear error message during typing
  };

  const validateCustomAmount = () => {
    if (!manualOffer) return;
    const numericValue = Number(customValue);
    let isValid = false;
    
    if (isNaN(numericValue)) {
      setErrorMessage(t('Veuillez entrer un montant valide'));
    } else if (numericValue < manualOffer.min) {
      setErrorMessage(t('Le montant minimum est de {{amount}}', {
        amount: formatAmounts(manualOffer.min),
      }));
    } else if (manualOffer.max && numericValue > manualOffer.max) {
      setErrorMessage(t('Le montant maximum est de {{amount}}', {
        amount: formatAmounts(manualOffer.max),
      }));
    } else {
      isValid = true;
      setErrorMessage('');
    }
    
    setIsCustomValid(isValid);
    setHasBlurred(true);
    if (isValid) {
      onSelectAmount(numericValue);
    } else {
      onSelectAmount(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      validateCustomAmount();
    }
  };
  
  useEffect(() => {
    if (customActive && customInputRef.current) {
      // Petit délai pour s'assurer que le DOM est mis à jour
      setTimeout(() => {
        customInputRef.current.focus();
      }, 0);
    }
  }, [customActive]);
  
  return (
    <div className={styles.container}>
      <h2 className=" h2 fieldsGroupHead" style={{ padding: 0, marginBottom: 24 }}>{t("Montant de la carte cadeau")}</h2>
      <div className={styles.amountGrid}>
        {amountOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={` ${styles.amountButton} ${selectedAmount === option.value ? `${styles.selected} btn` : ''}`}
            onClick={() => {
              setCustomActive(false);
              setCustomValue('');
              setErrorMessage(''); // Clear any error messages
              onSelectAmount(option.value);
            }}
          >
            {option.label}
            {option.isAmex && <span className={styles.amexIndicator}></span>}
          </button>
        ))}
        {manualOffer && (
          <>
            {customActive ? (
              <div style={{flex:1}}>
              <input
              ref={customInputRef}
                type="number"
                className={`${styles.customAmount} ${hasBlurred && isCustomValid ? styles.valid :
                  hasBlurred && isCustomValid === false ? styles.invalid : ''
                  }`}
                placeholder={t("Autre montant")}
                aria-label={t("Autre montant")}
                value={customValue}
                onChange={handleCustomValueChange}
                onBlur={validateCustomAmount}
                onKeyPress={handleKeyPress}
                min={manualOffer.min}
                max={manualOffer.max}
                  aria-describedby={errorMessage ? "error-custom-amount" : undefined}
              />
                <FieldError error={errorMessage} id="custom-amount" />
              </div>
            ) : (
                <button
                  className={`${styles.amountButton} ${selectedAmount === 'custom' ? styles.selected : ''}`}
                  onClick={handleCustomButtonClick}
                >
                  {t("Autre montant")}
                </button>
              )}
          </>
        )}
      </div>
    </div>
  );
};

GiftCardAmount.propTypes = {
  offers: PropTypes.arrayOf(PropTypes.object),
  selectedStore: PropTypes.string.isRequired,
  onSelectAmount: PropTypes.func.isRequired,
  selectedAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lang: PropTypes.string,
  store: PropTypes.object.isRequired,
};

export default GiftCardAmount;